import {
  moduleLevel,
  type AABB,
  type HideoutState,
  type MapBox,
  type MapDef,
  type Vec3,
} from "@rietz/shared";

/**
 * The bunker, as geometry: a MAP GENERATOR that turns the nine module levels
 * into a `MapDef` the existing machinery collides, renders and walks. A
 * generator rather than a JSON file because the geometry is a function of
 * progression (an unbuilt range is a tunnel plugged with rubble; a built one
 * has steel in it), so the map is rebuilt when `hideoutLayoutKey` changes.
 *
 * IN `map.solids`: the shell, blast door, pillars, door step, rubble plug and
 * (built range) target faces, posts and table — everything walkable or
 * shootable. OUT: every station, crate, drum and cable — decoration in
 * `render/hideoutProps.ts`, keeping the solid count at the size of the room.
 * `map.visuals` is empty. Conventions: y up, floor top at y = 0, yaw 0 looks
 * down −Z, every solid an AABB. Pure — no three.js, DOM or store.
 */

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/** `MapDef.id` of the generated shelter. `WorldView.loadMap` never sees another. */
export const HIDEOUT_MAP_ID = "hideout";

/**
 * How close the feet must be to a station's `interactBox` for F. Larger than
 * `LOOT_REACH_M` — nothing here is server-checked.
 */
export const STATION_REACH_M = 2.6;

/**
 * The nine modules in `HIDEOUT_MODULES` order — string literals, so this file
 * answers for a module before its content exists (`moduleLevel` returns 0).
 */
export const HIDEOUT_MODULE_ORDER: readonly string[] = [
  "lager",
  "generator",
  "werkbank",
  "sanitaet",
  "brennerei",
  "bienenstock",
  "schiessstand",
  "kraftraum",
  "funkraum",
];

/** The module whose level decides whether the tunnel is a range or a rockfall. */
const RANGE_MODULE_ID = "schiessstand";

// ---------------------------------------------------------------------------
// The shape of the answer
// ---------------------------------------------------------------------------

export interface HideoutStationSlot {
  moduleId: string;
  /** Floor-centre of the station footprint. */
  pos: Vec3;
  /** Facing, radians, same convention as `MapProp.yaw` (yaw 0 looks down −Z). */
  yaw: number;
  /** The volume F reaches for: ray-from-eye test + distance from the feet. */
  interactBox: AABB;
}

/** Steel plate, board silhouette or hanging gong — what the lane holds. */
export type RangeTargetKind = "plate" | "silhouette" | "gong";

export interface RangeTarget {
  id: string;
  kind: RangeTargetKind;
  /** The face — ALSO present in `map.solids`, so rounds stop in it. */
  box: AABB;
  /** Metres from `firingPoint`. */
  distM: number;
}

export interface HideoutLayout {
  map: MapDef;
  /** Feet position just inside the blast door, and the way the player faces. */
  spawn: Vec3;
  spawnYaw: number;
  /** ONE per module in `HIDEOUT_MODULE_ORDER`, built or not. */
  stations: readonly HideoutStationSlot[];
  /** Empty while the range is at level 0. */
  targets: readonly RangeTarget[];
  /** Where the shooter stands at the range table, facing down the tunnel. */
  firingPoint: Vec3;
  firingYaw: number;
  /** The standing area at the table: feet inside it means "at the range". */
  firingBox: AABB;
  /** Built level of all nine modules. */
  levels: Readonly<Record<string, number>>;
  /** How many of the nine are at level >= 1. */
  builtCount: number;
  /** `hideoutLayoutKey(state)` — the same string. */
  key: string;
}

// ---------------------------------------------------------------------------
// The shell, in numbers — every dimension named, so moving a wall is one edit.
// ---------------------------------------------------------------------------

/** Hall interior: 28 m across, 18 m deep, 4.2 m of clear height. */
export const HALL_MIN_X = -14;
export const HALL_MAX_X = 14;
export const HALL_MIN_Z = -9;
export const HALL_MAX_Z = 9;

/** Wall thickness. Walls sit OUTSIDE the interior extents above. */
const WALL_T = 0.6;
/** Floor slab: its top is y = 0, so the numbers above are also head room. */
const FLOOR_BOTTOM = -0.5;
/** Ceiling slab bottom (= clear height) and top. */
export const CEIL_Y = 4.2;
const CEIL_TOP = 4.8;

/** Outer face of each hall wall. */
const OUT_MIN_X = HALL_MIN_X - WALL_T;
const OUT_MAX_X = HALL_MAX_X + WALL_T;
const OUT_MIN_Z = HALL_MIN_Z - WALL_T;
const OUT_MAX_Z = HALL_MAX_Z + WALL_T;

/** Blast-door recess in the south wall: 2.4 m wide, 1.6 m deep, then the door. */
const BLAST_HALF_W = 1.2;
const BLAST_BACK_Z = HALL_MAX_Z + 1.6; // 10.6 — inner face of the door
const BLAST_DOOR_Z = BLAST_BACK_Z + 0.4; // 11.0 — outer face
/** Clear opening height of the door leaf; concrete above it. */
const BLAST_DOOR_H = 2.8;

/**
 * Ventilation niche east of the door, where the hives sit — one
 * `INTERACT_PAD_M` wider on each side, so the station's interact box never
 * reaches into a wall.
 */
const NICHE_MIN_X = 4.5;
const NICHE_MAX_X = 7.5;
const NICHE_BACK_Z = HALL_MAX_Z + 1.6;

/** Generator alcove cut into the north-east corner: 6 × 5 m, open to the hall. */
const GEN_MIN_X = 8;
const GEN_MAX_X = HALL_MAX_X;
const GEN_BACK_Z = HALL_MIN_Z - 5;

/** Range tunnel off the east wall. Interior 6 m wide, 3.2 m high, 41 m long. */
export const TUNNEL_HALF_W = 3;
export const TUNNEL_H = 3.2;
const TUNNEL_MIN_X = HALL_MAX_X; // the opening cuts through the east wall
export const TUNNEL_MAX_X = 55; // inner face of the bullet trap

/**
 * Where the shooter stands and the "at the range" pad — the whole width of the
 * firing line, since it doubles as the built range's `interactBox`.
 */
export const FIRING_X = HALL_MAX_X + 3;
const FIRING_HALF = 1.2;
const FIRING_HALF_Z = 2.2;
const FIRING_BOX_H = 2;

/**
 * The rubble plug's extent along the tunnel, starting past the east wall so the
 * station's `interactBox` never intersects it.
 */
const PLUG_MIN_X = HALL_MAX_X + 0.6;
const PLUG_MAX_X = 18;

/** How far a station footprint is grown in x and z to make its interact box. */
const INTERACT_PAD_M = 0.5;
/** Interact boxes are chest-high volumes standing on the floor. */
const INTERACT_TOP_Y = 2;

/** Feet position and facing at spawn: in the door recess, looking up the hall. */
const SPAWN: Vec3 = { x: 0, y: 0, z: HALL_MAX_Z + 0.1 };
const SPAWN_YAW = 0;

/** Slack added around every solid to make `map.bounds`. */
const BOUNDS_MARGIN_M = 1;

// ---------------------------------------------------------------------------
// Palette: a damp grey-green shelter, floor darker than walls, ceiling darker
// still. `mapMesh.ts` jitters per SOLID INDEX, which is why the long walls are
// chopped into several boxes — flat concrete 28 m long cannot look like
// anything.
// ---------------------------------------------------------------------------

const TINT_FLOOR = 0x767a77;
const TINT_WALL = 0x979d96;
const TINT_CEILING = 0x676c6a;
const TINT_DOOR = 0x3d4348;
const TINT_PILLAR = 0x878d86;
const TINT_STEP = 0x9aa09a;
const TINT_TRAP = 0x7a6a50;
const TINT_PLATE = 0xc6cbcf;
/** Weathered board, for the silhouettes. */
const TINT_BOARD = 0xd8d0b8;
/** Scarred brass, for the gong. */
const TINT_GONG = 0xb08d55;
const TINT_POST = 0x4a4f54;
const TINT_TABLE = 0xa88a63;

/** Rubble reads as a collapse and not as a wall only if the chunks differ. */
const TINT_RUBBLE_A = 0x6f6f68;
const TINT_RUBBLE_B = 0x7d8078;
const TINT_RUBBLE_C = 0x6a5c48;
const TINT_RUBBLE_D = 0x74776e;
const TINT_RUBBLE_E = 0x64655e;
const TINT_RUBBLE_F = 0x5b5c56;

// ---------------------------------------------------------------------------
// Stations
// ---------------------------------------------------------------------------

interface SlotSpec {
  moduleId: string;
  /** Floor centre. */
  x: number;
  z: number;
  yaw: number;
  /** Footprint half-extents. The interact box is these plus `INTERACT_PAD_M`. */
  hx: number;
  hz: number;
}

/**
 * Where each module stands. Three rules, all checked by `layout.test.ts`: an
 * `interactBox` never reaches into a wall, pillar or rubble; no two overlap;
 * every station borders open floor. Rule 1 is why nothing sits flush against
 * a wall — the furniture itself may, since it does not collide.
 */
const SLOTS: readonly SlotSpec[] = [
  // North wall, west of the alcove mouth: the shelving.
  { moduleId: "lager", x: 1.5, z: -8.0, yaw: Math.PI, hx: 2.0, hz: 0.35 },
  // Deep in the north-east alcove, facing back out into the hall.
  { moduleId: "generator", x: 11, z: -12, yaw: Math.PI, hx: 1.2, hz: 1.0 },
  // East wall, south of the tunnel mouth.
  { moduleId: "werkbank", x: 13.0, z: 5.5, yaw: Math.PI / 2, hx: 0.35, hz: 1.0 },
  // West wall, north half.
  { moduleId: "sanitaet", x: -13.0, z: -4.0, yaw: -Math.PI / 2, hx: 0.35, hz: 1.2 },
  // South-west corner, the still turned into the room.
  { moduleId: "brennerei", x: -11.5, z: 7.0, yaw: -Math.PI / 4, hx: 1.0, hz: 0.7 },
  // Under the vent grille in the south wall's niche.
  { moduleId: "bienenstock", x: 6.0, z: 9.4, yaw: 0, hx: 0.5, hz: 0.35 },
  // The tunnel mouth — the rubble, while the range is unbuilt. Once it is
  // built the slot MOVES to the firing point: see `RANGE_BUILT_SLOT`.
  { moduleId: "schiessstand", x: 13.2, z: 0, yaw: -Math.PI / 2, hx: 0.7, hz: 0.8 },
  // North-west quarter, in front of the corner.
  { moduleId: "kraftraum", x: -9.5, z: -6.5, yaw: -Math.PI * 0.85, hx: 1.2, hz: 0.85 },
  // South-east corner, the radio desk along the south wall.
  { moduleId: "funkraum", x: 11.0, z: 8.0, yaw: 0, hx: 0.9, hz: 0.4 },
];

/**
 * The range's station once BUILT: the firing point, past the tunnel mouth.
 * Its padded interact box IS the firing box, so range mode does not flicker.
 */
const RANGE_BUILT_SLOT: SlotSpec = {
  moduleId: RANGE_MODULE_ID,
  x: FIRING_X,
  z: 0,
  yaw: -Math.PI / 2,
  hx: FIRING_HALF - INTERACT_PAD_M,
  hz: FIRING_HALF_Z - INTERACT_PAD_M,
};

/**
 * Target placement: distance, lateral offset, kind. Chosen so that from the
 * firing point no target hides behind a nearer one and every face keeps clear
 * of the tunnel walls — moving one means re-checking the rest, which
 * `layout.test.ts` does.
 */
const RANGE_LEVEL_1: readonly (readonly [number, number, RangeTargetKind])[] = [
  [6, 2.1, "silhouette"],
  [8, -2.0, "plate"],
  [14, -2.1, "silhouette"],
  [16, 2.2, "plate"],
  [26, -1.0, "plate"],
  [30, 1.2, "plate"],
];

/** Level 2 ADDS — the targets the player learnt at level 1 do not move. */
const RANGE_LEVEL_2_EXTRA: readonly (readonly [number, number, RangeTargetKind])[] = [
  [10, 2.2, "plate"],
  [20, -1.8, "plate"],
  [22, 1.9, "silhouette"],
  [35, 0, "gong"],
];

/** Face size and post per kind: width, height, thickness, bottom y. A gong
 *  hangs from its frame (props), so `postT` 0 = no post. */
interface KindSpec {
  w: number;
  h: number;
  t: number;
  bottomY: number;
  material: MapBox["material"];
  tint: number;
  postT: number;
  postMaterial: MapBox["material"];
}

const KIND_SPECS: Readonly<Record<RangeTargetKind, KindSpec>> = {
  plate: {
    w: 0.5,
    h: 0.5,
    t: 0.06,
    bottomY: 1.1,
    material: "metal",
    tint: TINT_PLATE,
    postT: 0.1,
    postMaterial: "metal",
  },
  silhouette: {
    w: 0.6,
    h: 1.2,
    t: 0.05,
    bottomY: 0.5,
    material: "wood",
    tint: TINT_BOARD,
    postT: 0.08,
    postMaterial: "wood",
  },
  gong: {
    w: 0.9,
    h: 0.9,
    t: 0.06,
    bottomY: 0.9,
    material: "metal",
    tint: TINT_GONG,
    postT: 0,
    postMaterial: "metal",
  },
};

// ---------------------------------------------------------------------------
// Building
// ---------------------------------------------------------------------------

/**
 * The whole shelter for one hideout state. Cheap, but the session should only
 * call it when `hideoutLayoutKey` moves — re-colliding and re-meshing is not.
 */
export function buildHideoutLayout(state: HideoutState): HideoutLayout {
  const levels: Record<string, number> = {};
  let builtCount = 0;
  for (const id of HIDEOUT_MODULE_ORDER) {
    const level = moduleLevel(state, id);
    levels[id] = level;
    if (level >= 1) builtCount += 1;
  }

  const solids: MapBox[] = [];
  addShell(solids);
  addTunnel(solids);

  const targets: RangeTarget[] = [];
  const rangeLevel = levels[RANGE_MODULE_ID] ?? 0;
  if (rangeLevel <= 0) addRubblePlug(solids);
  else addRange(solids, targets, rangeLevel);

  const map: MapDef = {
    id: HIDEOUT_MAP_ID,
    name: "Der Bunker",
    bounds: boundsOf(solids, BOUNDS_MARGIN_M),
    // Not a raid. Nothing counts down here and nothing extracts; the session
    // drives the loop with the socket muted and the HUD's raid clock hidden.
    raidLengthSec: 0,
    solids,
    playerSpawns: [{ x: SPAWN.x, y: SPAWN.y, z: SPAWN.z }],
    extracts: [],
    waypoints: { nodes: [], edges: [] },
    // See the header: the shelter's furniture is three.js, not `MapProp`.
    visuals: [],
  };

  return {
    map,
    spawn: { x: SPAWN.x, y: SPAWN.y, z: SPAWN.z },
    spawnYaw: SPAWN_YAW,
    stations: SLOTS.map((spec) =>
      stationOf(rangeLevel >= 1 && spec.moduleId === RANGE_MODULE_ID ? RANGE_BUILT_SLOT : spec),
    ),
    targets,
    firingPoint: { x: FIRING_X, y: 0, z: 0 },
    // Down the tunnel is +X, and yaw 0 looks down −Z, so this is a right turn.
    firingYaw: -Math.PI / 2,
    firingBox: aabb(
      FIRING_X - FIRING_HALF,
      0,
      -FIRING_HALF_Z,
      FIRING_X + FIRING_HALF,
      FIRING_BOX_H,
      FIRING_HALF_Z,
    ),
    levels,
    builtCount,
    key: hideoutLayoutKey(state),
  };
}

/**
 * The one string that says "the shelter changed shape": a join over the nine
 * levels only — fuel and crafts move constantly and move no wall.
 */
export function hideoutLayoutKey(state: HideoutState): string {
  let key = "";
  for (const id of HIDEOUT_MODULE_ORDER) {
    if (key.length > 0) key += "|";
    key += id + String(moduleLevel(state, id));
  }
  return key;
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

function aabb(
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
): AABB {
  return { min: { x: minX, y: minY, z: minZ }, max: { x: maxX, y: maxY, z: maxZ } };
}

/** Appends one solid. Every wall, slab and face in this file goes through here. */
function push(
  out: MapBox[],
  material: MapBox["material"],
  colorTint: number,
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
): void {
  out.push({ box: aabb(minX, minY, minZ, maxX, maxY, maxZ), material, colorTint });
}

/**
 * Splits one long wall into `n` boxes, purely for the look: `mapMesh.ts`
 * jitters colour per solid index.
 */
function pushRun(
  out: MapBox[],
  material: MapBox["material"],
  colorTint: number,
  axis: "x" | "z",
  from: number,
  to: number,
  n: number,
  a0: number,
  a1: number,
  minY: number,
  maxY: number,
): void {
  const step = (to - from) / n;
  for (let i = 0; i < n; i++) {
    const lo = from + step * i;
    const hi = i === n - 1 ? to : from + step * (i + 1);
    if (axis === "x") push(out, material, colorTint, lo, minY, a0, hi, maxY, a1);
    else push(out, material, colorTint, a0, minY, lo, a1, maxY, hi);
  }
}

/** The union of every solid, grown by `margin`. Players are clamped to this. */
function boundsOf(solids: readonly MapBox[], margin: number): AABB {
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (const s of solids) {
    if (s.box.min.x < minX) minX = s.box.min.x;
    if (s.box.min.y < minY) minY = s.box.min.y;
    if (s.box.min.z < minZ) minZ = s.box.min.z;
    if (s.box.max.x > maxX) maxX = s.box.max.x;
    if (s.box.max.y > maxY) maxY = s.box.max.y;
    if (s.box.max.z > maxZ) maxZ = s.box.max.z;
  }
  return aabb(
    minX - margin,
    minY - margin,
    minZ - margin,
    maxX + margin,
    maxY + margin,
    maxZ + margin,
  );
}

function stationOf(spec: SlotSpec): HideoutStationSlot {
  return {
    moduleId: spec.moduleId,
    pos: { x: spec.x, y: 0, z: spec.z },
    yaw: spec.yaw,
    interactBox: aabb(
      spec.x - spec.hx - INTERACT_PAD_M,
      0,
      spec.z - spec.hz - INTERACT_PAD_M,
      spec.x + spec.hx + INTERACT_PAD_M,
      INTERACT_TOP_Y,
      spec.z + spec.hz + INTERACT_PAD_M,
    ),
  };
}

// ---------------------------------------------------------------------------
// The shell
// ---------------------------------------------------------------------------

/**
 * Floor, ceiling, walls, alcoves, blast door and pillars — level-independent.
 * The slabs are two boxes covering hall AND recesses; the overhang is buried
 * behind the walls, and five hugging slabs would need their seams kept in step.
 */
function addShell(out: MapBox[]): void {
  // --- slabs ---------------------------------------------------------------
  push(out, "concrete", TINT_FLOOR, OUT_MIN_X, FLOOR_BOTTOM, OUT_MIN_Z, OUT_MAX_X, 0, BLAST_DOOR_Z + 0.05);
  push(out, "concrete", TINT_CEILING, OUT_MIN_X, CEIL_Y, OUT_MIN_Z, OUT_MAX_X, CEIL_TOP, BLAST_DOOR_Z + 0.05);
  // ...and the same pair over the generator alcove.
  push(out, "concrete", TINT_FLOOR, GEN_MIN_X - WALL_T, FLOOR_BOTTOM, GEN_BACK_Z - WALL_T, OUT_MAX_X, 0, OUT_MIN_Z);
  push(out, "concrete", TINT_CEILING, GEN_MIN_X - WALL_T, CEIL_Y, GEN_BACK_Z - WALL_T, OUT_MAX_X, CEIL_TOP, OUT_MIN_Z);

  // --- west wall, in four panels -------------------------------------------
  pushRun(out, "concrete", TINT_WALL, "z", OUT_MIN_Z, OUT_MAX_Z, 4, OUT_MIN_X, HALL_MIN_X, FLOOR_BOTTOM, CEIL_TOP);

  // --- north wall: from the west wall to the alcove's west jamb ------------
  pushRun(out, "concrete", TINT_WALL, "x", HALL_MIN_X, GEN_MIN_X, 4, OUT_MIN_Z, HALL_MIN_Z, FLOOR_BOTTOM, CEIL_TOP);

  // --- east wall, broken by the tunnel mouth; north of it runs to the alcove's
  // back wall.
  pushRun(
    out, "concrete", TINT_WALL, "z",
    GEN_BACK_Z - WALL_T, -TUNNEL_HALF_W, 3,
    HALL_MAX_X, OUT_MAX_X, FLOOR_BOTTOM, CEIL_TOP,
  );
  pushRun(out, "concrete", TINT_WALL, "z", TUNNEL_HALF_W, OUT_MAX_Z, 2, HALL_MAX_X, OUT_MAX_X, FLOOR_BOTTOM, CEIL_TOP);

  // --- south wall, broken by the door recess and the vent niche ------------
  pushRun(out, "concrete", TINT_WALL, "x", HALL_MIN_X, -BLAST_HALF_W, 3, HALL_MAX_Z, OUT_MAX_Z, FLOOR_BOTTOM, CEIL_TOP);
  push(out, "concrete", TINT_WALL, BLAST_HALF_W, FLOOR_BOTTOM, HALL_MAX_Z, NICHE_MIN_X, CEIL_TOP, OUT_MAX_Z);
  pushRun(out, "concrete", TINT_WALL, "x", NICHE_MAX_X, HALL_MAX_X, 3, HALL_MAX_Z, OUT_MAX_Z, FLOOR_BOTTOM, CEIL_TOP);

  // --- blast-door recess ---------------------------------------------------
  push(out, "concrete", TINT_WALL, -BLAST_HALF_W - WALL_T, FLOOR_BOTTOM, OUT_MAX_Z, -BLAST_HALF_W, CEIL_TOP, BLAST_DOOR_Z);
  push(out, "concrete", TINT_WALL, BLAST_HALF_W, FLOOR_BOTTOM, OUT_MAX_Z, BLAST_HALF_W + WALL_T, CEIL_TOP, BLAST_DOOR_Z);
  // The door itself: shut, and staying shut. It is the reason the shelter is a
  // shelter — there is no way out of this map and none is wanted.
  push(out, "metal", TINT_DOOR, -BLAST_HALF_W, FLOOR_BOTTOM, BLAST_BACK_Z, BLAST_HALF_W, BLAST_DOOR_H, BLAST_DOOR_Z);
  push(out, "concrete", TINT_WALL, -BLAST_HALF_W, BLAST_DOOR_H, BLAST_BACK_Z, BLAST_HALF_W, CEIL_TOP, BLAST_DOOR_Z);
  // A step up to the threshold. Low enough that `STEP_UP_MAX` (0.45 m) walks
  // it without the player ever knowing it is there.
  push(out, "concrete", TINT_STEP, -BLAST_HALF_W, 0, OUT_MAX_Z, BLAST_HALF_W, 0.15, BLAST_BACK_Z);

  // --- ventilation niche ---------------------------------------------------
  push(out, "concrete", TINT_WALL, NICHE_MIN_X - WALL_T, FLOOR_BOTTOM, OUT_MAX_Z, NICHE_MIN_X, CEIL_TOP, NICHE_BACK_Z + WALL_T);
  push(out, "concrete", TINT_WALL, NICHE_MAX_X, FLOOR_BOTTOM, OUT_MAX_Z, NICHE_MAX_X + WALL_T, CEIL_TOP, NICHE_BACK_Z + WALL_T);
  push(out, "concrete", TINT_WALL, NICHE_MIN_X, FLOOR_BOTTOM, NICHE_BACK_Z, NICHE_MAX_X, CEIL_TOP, NICHE_BACK_Z + WALL_T);

  // --- generator alcove ----------------------------------------------------
  push(out, "concrete", TINT_WALL, GEN_MIN_X - WALL_T, FLOOR_BOTTOM, GEN_BACK_Z, GEN_MIN_X, CEIL_TOP, OUT_MIN_Z);
  push(out, "concrete", TINT_WALL, GEN_MIN_X - WALL_T, FLOOR_BOTTOM, GEN_BACK_Z - WALL_T, GEN_MAX_X, CEIL_TOP, GEN_BACK_Z);

  // --- pillars: four, to give the eye something to move against.
  for (const px of [-7, 7]) {
    for (const pz of [-3, 3]) {
      push(out, "concrete", TINT_PILLAR, px - 0.3, 0, pz - 0.3, px + 0.3, CEIL_Y, pz + 0.3);
    }
  }
}

/**
 * The range tunnel's shell, built at every level — a plug that doubled as the
 * back wall would have to be rebuilt when the range finished.
 */
function addTunnel(out: MapBox[]): void {
  const outerZ = TUNNEL_HALF_W + WALL_T;
  const startX = OUT_MAX_X;
  const endX = TUNNEL_MAX_X + WALL_T;

  push(out, "concrete", TINT_FLOOR, startX, FLOOR_BOTTOM, -outerZ, endX, 0, outerZ);
  push(out, "concrete", TINT_CEILING, TUNNEL_MIN_X, TUNNEL_H, -TUNNEL_HALF_W, endX, CEIL_Y, TUNNEL_HALF_W);
  pushRun(out, "concrete", TINT_WALL, "x", startX, endX, 5, -outerZ, -TUNNEL_HALF_W, FLOOR_BOTTOM, CEIL_Y);
  pushRun(out, "concrete", TINT_WALL, "x", startX, endX, 5, TUNNEL_HALF_W, outerZ, FLOOR_BOTTOM, CEIL_Y);

  // The bullet trap: sandbags and dirt, so a round that misses everything ends
  // in something that reads as a backstop rather than in bare concrete.
  push(out, "dirt", TINT_TRAP, TUNNEL_MAX_X, FLOOR_BOTTOM, -TUNNEL_HALF_W, endX, CEIL_Y, TUNNEL_HALF_W);
}

/**
 * The collapse in the tunnel mouth while the range is unbuilt. SEALED is the
 * requirement (`layout.test.ts` samples the whole cross-section): the first
 * box spans the full width and more than the full height; the rest is
 * silhouette — chunks spilling out at different heights across the 6 m.
 */
function addRubblePlug(out: MapBox[]): void {
  const zi = TUNNEL_HALF_W;
  push(out, "concrete", TINT_RUBBLE_A, 15.2, 0, -zi, 17.0, TUNNEL_H + 0.1, zi);
  push(out, "stone", TINT_RUBBLE_B, PLUG_MIN_X, 0, -zi, 15.6, 2.2, -0.6);
  push(out, "dirt", TINT_RUBBLE_C, PLUG_MIN_X, 0, -0.2, 15.4, 1.5, zi);
  push(out, "stone", TINT_RUBBLE_D, 17.0, 0, -zi, PLUG_MAX_X, 2.6, -0.4);
  push(out, "concrete", TINT_RUBBLE_E, 17.0, 0, 0.2, 17.8, 1.8, zi);
  push(out, "concrete", TINT_RUBBLE_F, 14.8, 2.0, -1.6, 15.6, TUNNEL_H + 0.1, 2.0);
  push(out, "stone", TINT_RUBBLE_B, 17.0, 2.2, -2.4, 17.6, TUNNEL_H, 0.8);
  push(out, "dirt", TINT_RUBBLE_D, 15.0, 1.2, 1.4, 15.8, 2.2, zi);
}

/**
 * The range proper: target faces, posts and the bench. A face goes into
 * `map.solids` AND `targets` as the SAME `AABB`, so a round stopping and the
 * drill counting answer for one box.
 */
function addRange(out: MapBox[], targets: RangeTarget[], level: number): void {
  const spec = level >= 2 ? [...RANGE_LEVEL_1, ...RANGE_LEVEL_2_EXTRA] : [...RANGE_LEVEL_1];
  spec.sort((a, b) => a[0] - b[0]);
  for (const [distM, offZ, kind] of spec) {
    const k = KIND_SPECS[kind];
    const cx = FIRING_X + distM;
    const box = aabb(
      cx - k.t / 2,
      k.bottomY,
      offZ - k.w / 2,
      cx + k.t / 2,
      k.bottomY + k.h,
      offZ + k.w / 2,
    );
    out.push({ box, material: k.material, colorTint: k.tint });
    // The post is not a target: it is what makes the face read as a target
    // standing in a lane rather than as a shape floating in the dark.
    if (k.postT > 0) {
      const hp = k.postT / 2;
      push(out, k.postMaterial, TINT_POST, cx - hp, 0, offZ - hp, cx + hp, k.bottomY, offZ + hp);
    }
    targets.push({ id: `${kind}:${distM}`, kind, box, distM });
  }

  // The bench to the shooter's right, hard against the wall — the lane and the
  // firing box stay clear.
  push(out, "wood", TINT_TABLE, FIRING_X - 0.6, 0, 2.4, FIRING_X + 0.6, 0.9, TUNNEL_HALF_W);
}
